import React from 'react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { X, AlertTriangle, Info, CheckCircle, XCircle, Crown, Zap, ShieldAlert, Package2 } from 'lucide-react';
import { formatNumber, formatLargeNumber } from '../../utils/number';

export interface WarningBannerProps {
  variant?: 'warning' | 'error' | 'info' | 'success' | 'premium' | 'limit';
  title?: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'outline' | 'ghost';
  };
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  className?: string;
  animated?: boolean;
}

const variantConfigs = {
  warning: {
    container: 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-amber-950/30 border-amber-200/60 dark:border-amber-800/60',
    border: 'border-l-4 border-l-amber-400 dark:border-l-amber-500',
    icon: <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />,
    title: 'text-amber-900 dark:text-amber-200 font-semibold',
    message: 'text-amber-800 dark:text-amber-300',
    actionDefault: 'bg-amber-600 hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600 text-white shadow-sm',
    actionOutline: 'border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/50',
  },
  error: {
    container: 'bg-gradient-to-r from-red-50 via-rose-50 to-red-50 dark:from-red-950/30 dark:via-rose-950/30 dark:to-red-950/30 border-red-200/60 dark:border-red-800/60',
    border: 'border-l-4 border-l-red-400 dark:border-l-red-500',
    icon: <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />,
    title: 'text-red-900 dark:text-red-200 font-semibold',
    message: 'text-red-800 dark:text-red-300',
    actionDefault: 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white shadow-sm',
    actionOutline: 'border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/50',
  },
  info: {
    container: 'bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 dark:from-blue-950/30 dark:via-sky-950/30 dark:to-blue-950/30 border-blue-200/60 dark:border-blue-800/60',
    border: 'border-l-4 border-l-blue-400 dark:border-l-blue-500',
    icon: <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />,
    title: 'text-blue-900 dark:text-blue-200 font-semibold',
    message: 'text-blue-800 dark:text-blue-300',
    actionDefault: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white shadow-sm',
    actionOutline: 'border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/50',
  },
  success: {
    container: 'bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 dark:from-green-950/30 dark:via-emerald-950/30 dark:to-green-950/30 border-green-200/60 dark:border-green-800/60',
    border: 'border-l-4 border-l-green-400 dark:border-l-green-500',
    icon: <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />,
    title: 'text-green-900 dark:text-green-200 font-semibold',
    message: 'text-green-800 dark:text-green-300',
    actionDefault: 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white shadow-sm',
    actionOutline: 'border-green-300 dark:border-green-600 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-950/50',
  },
  premium: {
    container: 'bg-gradient-to-r from-purple-50 via-violet-50 to-purple-50 dark:from-purple-950/30 dark:via-violet-950/30 dark:to-purple-950/30 border-purple-200/60 dark:border-purple-800/60',
    border: 'border-l-4 border-l-purple-400 dark:border-l-purple-500',
    icon: <Crown className="h-5 w-5 text-purple-600 dark:text-purple-400" />,
    title: 'text-purple-900 dark:text-purple-200 font-semibold',
    message: 'text-purple-800 dark:text-purple-300',
    actionDefault: 'bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 dark:from-purple-700 dark:to-violet-700 dark:hover:from-purple-600 dark:hover:to-violet-600 text-white shadow-sm',
    actionOutline: 'border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/50',
  },
  limit: {
    container: 'bg-gradient-to-r from-orange-50 via-red-50 to-orange-50 dark:from-orange-950/30 dark:via-red-950/30 dark:to-orange-950/30 border-orange-200/60 dark:border-orange-800/60',
    border: 'border-l-4 border-l-orange-400 dark:border-l-orange-500',
    icon: <ShieldAlert className="h-5 w-5 text-orange-600 dark:text-orange-400" />,
    title: 'text-orange-900 dark:text-orange-200 font-semibold',
    message: 'text-orange-800 dark:text-orange-300',
    actionDefault: 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 dark:from-orange-700 dark:to-red-700 dark:hover:from-orange-600 dark:hover:to-red-600 text-white shadow-sm',
    actionOutline: 'border-orange-300 dark:border-orange-600 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950/50',
  },
};

export function WarningBanner({
  variant = 'warning',
  title,
  message,
  action,
  dismissible = false,
  onDismiss,
  icon,
  className,
  animated = true,
}: WarningBannerProps) {
  const config = variantConfigs[variant];

  return (
    <div
      className={cn(
        // Base styles
        'relative overflow-hidden rounded-xl border border-solid p-6 shadow-sm backdrop-blur-sm',
        // Variant-specific container
        config.container,
        config.border,
        // Animation
        animated && 'animate-in slide-in-from-top-2 duration-300',
        className
      )}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-20" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-20" />
      </div>

      <div className="relative flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {icon || config.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className={cn('text-sm mb-1', config.title)}>
              {title}
            </h3>
          )}
          <p className={cn('text-sm leading-relaxed', config.message)}>
            {message}
          </p>

          {/* Action button */}
          {action && (
            <div className="mt-4">
              <Button
                size="sm"
                variant={action.variant || 'default'}
                onClick={action.onClick}
                className={cn(
                  'h-8 px-4 text-xs font-medium transition-all duration-200 hover:scale-105',
                  action.variant === 'outline' ? config.actionOutline : config.actionDefault
                )}
              >
                {action.label}
              </Button>
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {dismissible && onDismiss && (
          <button
            onClick={onDismiss}
            className={cn(
              'flex-shrink-0 p-1 rounded-md transition-colors duration-200',
              'hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2',
              config.message.replace('text-', 'text-').replace('-800', '-600'),
              'focus:ring-current'
            )}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Subtle glow effect */}
      <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-black/5 pointer-events-none" />
    </div>
  );
}

// Specific banner components for common use cases
export function ConnectorLimitBanner({
  currentConnectors,
  maxConnectors,
  planName,
  onUpgrade,
  className,
}: {
  currentConnectors: number;
  maxConnectors: number;
  planName: string;
  onUpgrade: () => void;
  className?: string;
}) {
  const isOverLimit = currentConnectors > maxConnectors;

  return (
    <WarningBanner
      variant={isOverLimit ? "limit" : "premium"}
      title={isOverLimit ? "Connector Limit Exceeded" : "Connector Limit Reached"}
      message={
        isOverLimit
          ? `You have ${currentConnectors} connectors but your ${planName} plan only allows ${maxConnectors}. Please upgrade to continue using all your connectors.`
          : `You've used all ${maxConnectors} connector slots in your ${planName} plan. Upgrade to add more connectors.`
      }
      action={{
        label: "Upgrade Plan",
        onClick: onUpgrade,
      }}
      icon={<Crown className="h-5 w-5 text-orange-600" />}
      className={className}
    />
  );
}

export function UsageLimitBanner({
  currentUsage,
  usageLimit,
  usagePercentage,
  planName,
  onUpgrade,
  className,
}: {
  currentUsage: number;
  usageLimit: number;
  usagePercentage: number;
  planName: string;
  onUpgrade: () => void;
  className?: string;
}) {
  const isNearLimit = usagePercentage >= 80;
  const isOverLimit = usagePercentage >= 100;
  
  if (!isNearLimit) return null;
  
  return (
    <WarningBanner
      variant={isOverLimit ? "error" : "warning"}
      title={isOverLimit ? "Usage Limit Exceeded" : "Usage Limit Warning"}
      message={
        isOverLimit
          ? `You've exceeded your ${planName} plan limit (${currentUsage}/${usageLimit} credits used). Upgrade to continue processing images.`
          : `You're using ${usagePercentage.toFixed(0)}% of your ${planName} plan (${currentUsage}/${usageLimit} credits). Consider upgrading before reaching the limit.`
      }
      action={{
        label: isOverLimit ? "Upgrade Now" : "View Plans",
        onClick: onUpgrade,
      }}
      icon={<Zap className="h-5 w-5 text-amber-600" />}
      className={className}
    />
  );
}

export function SyncLimitBanner({
  currentSyncs,
  syncLimit,
  planName,
  onUpgrade,
  className,
}: {
  currentSyncs: number;
  syncLimit: number;
  planName: string;
  onUpgrade: () => void;
  className?: string;
}) {
  const remaining = syncLimit - currentSyncs;
  const isNearLimit = remaining <= 1;
  const isOverLimit = remaining <= 0;
  
  if (!isNearLimit) return null;
  
  return (
    <WarningBanner
      variant={isOverLimit ? "error" : "warning"}
      title={isOverLimit ? "Monthly Sync Limit Reached" : "Monthly Sync Limit Warning"}
      message={
        isOverLimit
          ? `You've used all ${syncLimit} syncs for this month on your ${planName} plan. Upgrade to get more syncs.`
          : `You have ${remaining} sync remaining this month on your ${planName} plan (${currentSyncs}/${syncLimit} used).`
      }
      action={{
        label: "Upgrade Plan",
        onClick: onUpgrade,
      }}
      className={className}
    />
  );
}

export function JobProcessingBanner({
  jobCount,
  jobType,
  onMonitor,
  className,
}: {
  jobCount: number;
  jobType: 'creation' | 'processing' | 'uploading';
  onMonitor?: () => void;
  className?: string;
}) {
  const configs = {
    creation: {
      title: "Creating Processing Jobs",
      message: `Processing ${jobCount} products into processing jobs...`,
      icon: <Zap className="h-5 w-5 animate-pulse text-blue-600" />,
    },
    processing: {
      title: "Processing Images",
      message: `${jobCount} images are currently being processed...`,
      icon: <Zap className="h-5 w-5 animate-pulse text-orange-600" />,
    },
    uploading: {
      title: "Uploading to Store",
      message: `${jobCount} processed images are being uploaded to your store...`,
      icon: <Zap className="h-5 w-5 animate-pulse text-green-600" />,
    },
  };
  
  const config = configs[jobType];
  
  return (
    <WarningBanner
      variant="info"
      title={config.title}
      message={config.message}
      action={onMonitor ? {
        label: "Monitor Progress",
        onClick: onMonitor,
        variant: "outline",
      } : undefined}
      icon={config.icon}
      className={className}
    />
  );
}

export function BulkSelectionBanner({
  selectedCount,
  totalCount,
  category,
  stats,
  remaining,
  limitApplied,
  onReview,
  onClear,
  className,
}: {
  selectedCount: number;
  totalCount: number;
  category?: string | null;
  stats?: { total: number; untransformed: number; partial: number; complete: number } | null;
  remaining?: number;
  limitApplied?: boolean;
  onReview: () => void;
  onClear: () => void;
  className?: string;
}) {
  // Build message with transformation stats
  let message = `You have ${selectedCount} products selected for bulk processing`;
  if (category) {
    message += ` from category: ${category}`;
  }
  message += '.';

  // Add stats information if available
  if (stats) {
    const transformedCount = stats.complete + stats.partial;
    if (transformedCount > 0) {
      message += ` (${stats.complete} fully transformed, ${stats.partial} partially transformed, ${stats.untransformed} untransformed in this category)`;
    }
  }

  // Add remaining info
  if (remaining && remaining > 0) {
    message += ` ${remaining} more products available for next batch.`;
  }

  // Add limit warning
  if (limitApplied) {
    message += ' Selection limit applied - process this batch then select more.';
  }

  return (
    <WarningBanner
      variant="info"
      title={`${selectedCount} products selected`}
      message={message}
      action={{
        label: "Review & Process",
        onClick: onReview,
      }}
      dismissible
      onDismiss={onClear}
      icon={<Package2 className="h-5 w-5 text-blue-600" />}
      className={className}
    />
  );
}

export function CreditLimitBanner({
  required,
  remaining,
  current,
  limit,
  planName,
  products,
  images,
  creditsPerImage,
  onUpgrade,
  className,
}: {
  required: number;
  remaining: number;
  current: number;
  limit: number;
  planName: string;
  products: number;
  images: number;
  creditsPerImage: number;
  onUpgrade: () => void;
  className?: string;
}) {
  return (
    <WarningBanner
      variant="limit"
      title="Credit Limit Exceeded"
      message={`This batch requires ${formatNumber(required)} credits but you only have ${formatNumber(remaining)} remaining. Your ${planName} plan includes ${formatLargeNumber(limit)} credits per month (${formatNumber(current)} already used). Processing ${formatLargeNumber(products)} products with ${formatLargeNumber(images)} images at ${formatNumber(creditsPerImage, 1)} credits per image.`}
      action={{
        label: "Upgrade Plan",
        onClick: onUpgrade,
      }}
      icon={<ShieldAlert className="h-5 w-5 text-orange-600" />}
      className={className}
    />
  );
}